import { store } from "../../../../../redux/store";
import { t } from "../../../../core/translate";
import { selectors as brandSelectors } from "../../../../../redux/brand";
import { selectors as localeSelectors } from "../../../../../redux/locale";
import { selectors as routerSelectors } from "../../../../../redux/router";
import { actions as userActions } from "../../../../../redux/user";
import { actions as modalActions } from "../../../../../redux/modals";
import {
  ssoToOldOnlineURL,
  storeAndRedirect
} from "../../../../utils/location";
import { gaEvent } from "../../../../utils/analytics";

import MenuItem from "../../../../components/list-item";
import { saveUserData } from "../../../../utils/users";
import { currentUser, navigateTo } from "./shared";
import getlocale from "../../../../../utils/locales";

export const userInfo = () => {
  const state = store.getState();
  const ssoOnly = brandSelectors.getBrandValue(
    state,
    "AccountCompletion.ssoOnly"
  );
  const showUserInfo = brandSelectors.getBrandValue(state, "Menu.user_info");
  const isFullAccountUser = !ssoOnly && !currentUser().ssoOnly;
  const linkToUserProfile = brandSelectors.getBrandValue(
    state,
    "Locations.AccountCenter"
  );

  return MenuItem({
    condition: showUserInfo && isFullAccountUser,
    onClick: event => {
      navigateTo(event, linkToUserProfile, "user-info");
    },
    text: t("main-menu.user-info", "User Info"),
    href: linkToUserProfile,
    target: "_blank"
  });
};

export const devices = () => {
  const state = store.getState();
  const showDevices = brandSelectors.getBrandValue(state, "Menu.devices");
  const linkToDevices = brandSelectors.getBrandValue(
    state,
    "Locations.Devices"
  );

  return MenuItem({
    condition: showDevices,
    onClick: event => navigateTo(event, linkToDevices, "devices"),
    text: t("main-menu.devices", "Devices"),
    href: linkToDevices,
    target: "_blank"
  });
};

export const resources = () => {
  const state = store.getState();
  const showResources = brandSelectors.getBrandValue(state, "Menu.resources");
  const linkToResources = brandSelectors.getBrandValue(
    state,
    "Locations.Resources"
  );

  return MenuItem({
    condition: showResources && currentUser().hasAncillaries,
    onClick: event => {
      navigateTo(event, linkToResources, "resources");
    },
    text: t("main-menu.resources", "Resources"),
    href: linkToResources,
    target: "_blank"
  });
};

export const notifications = () => {
  return MenuItem({
    onClick: () => alert("Implement Me"),
    text: t("main-menu.notifications", "Notifications")
  });
};

export const languages = onClick => {
  if (onClick) {
    return MenuItem({
      onClick,
      text: i18n.t("base.language-choice", "Languages")
    });
  }

  const state = store.getState();
  const dispatch = store.dispatch;
  const currentLanguage = localeSelectors.getCurrentLanguage(state);
  // TODO - Add language flag to menu item title
  return MenuItem({
    onClick: event => {
      event.preventDefault();
      gaEvent("header/languages-modal");
      dispatch(
        modalActions.openModal("LanguagesModal", {
          title: t("base.language-choice", "Languages"),
          wrapWithDialog: true
        })
      );
    },
    text: t("base.language-choice", "Languages")
  });
};

export const pod = () => {
  const state = store.getState();
  const showPOD = brandSelectors.getBrandValue(state, "Menu.pod");
  const linkToPOD = brandSelectors.getBrandValue(state, "Locations.POD");

  return MenuItem({
    condition: showPOD && currentUser().userHasPOD,
    onClick: event => {
      navigateTo(event, linkToPOD, "pod");
    },
    text: t("main-menu.print-on-demand", "Print on Demand"),
    href: linkToPOD,
    target: "_blank"
  });
};

export const completeAccount = () => {
  const state = store.getState();
  const ssoOnly = brandSelectors.getBrandValue(
    state,
    "AccountCompletion.ssoOnly"
  );

  return MenuItem({
    condition: !ssoOnly && currentUser().ssoOnly,
    onClick: event => {
      event.preventDefault();
      gaEvent("header/complete-account");
      saveUserData(null, "ssoTimeout", Date.now());
      storeAndRedirect("/user/sso");
    },
    text: t("sso.complete-your-account", "Complete Your Account"),
    href: "#",
    target: "_blank"
  });
};

export const optOut = () => {
  const state = store.getState();
  const showOptOut = brandSelectors.getBrandValue(state, "OptOut.enabled");
  const linkToOptOut = brandSelectors.getBrandValue(state, "OptOut.url");

  const _buildOptOutUrl = () => {
    let url = linkToOptOut;
    const tokens = currentUser().contextTokens;
    if (url.slice(-1) !== "/") {
      url += "/";
    }
    url += "sso/";
    if (tokens && Object.keys(tokens).length > 0) {
      const key = Object.keys(tokens)[0];
      const token = tokens[key];
      url += token;
    }
    return url;
  };

  return MenuItem({
    condition: showOptOut,
    onClick: event => {
      event.preventDefault();
      gaEvent(`header/opt-out`);
      ssoToOldOnlineURL(_buildOptOutUrl(), true, currentUser());
    },
    text: t("main-menu.opt-out", "Opt Out"),
    href: "#",
    target: "_blank"
  });
};

export const cookieSettings = () => {
  const state = store.getState();
  const isNative = routerSelectors.isNative(state);
  const theme = Fraction.Brand.Theme.primary;
  const locale = getlocale(i18n.getCurrentLocale());

  return MenuItem({
    condition:
      brandSelectors.getBrandValue(state, "Cookies.notify") && !isNative,
    onClick: event => {
      event.preventDefault();
      gaEvent("header/cookies");
      //optional chaining no supported in fraction
      if (window.CookieCat && window.CookieCat.showBanner) {
        window.CookieCat.showBanner({
          app: "fraction",
          theme: theme,
          locale: locale.rfc
        });
      }
    },
    text: t("cookies.settings", "Cookie Settings")
  });
};

export const signout = () => {
  const state = store.getState();
  const dispatch = store.dispatch;
  const showSignout = brandSelectors.getBrandValue(state, "Menu.sign_out");
  const signoutRedirect = brandSelectors.getBrandValue(
    state,
    "Locations.signout_redirect"
  );

  return MenuItem({
    condition: showSignout,
    onClick: event => {
      event.preventDefault();
      gaEvent("header/logout");
      dispatch(userActions.signOut(currentUser(), signoutRedirect));
    },
    text: t("reader.settings.signout", "Sign Out"),
    href: signoutRedirect
  });
};

// Branding intentionally omitted due to complexities around customer vs whitelabel
// stores and independent user purchases. May need to revisit later.
export const subscriptions = () => {
  return MenuItem({
    condition: currentUser().hasRecurringSubscription,
    onClick: event => {
      const host =
        Fraction.Environment.env === "production"
          ? "www.vitalsource.com"
          : "stargate-dev.gc.vitalbook.com";
      navigateTo(
        event,
        `https://${host}/subscriptions?utm_source=bookshelfonline&utm_medium=bookshelf`,
        "manage-subs"
      );
    },
    text: t("main-menu.manage-subs", "Manage Subscriptions")
  });
};
